import {useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Breadcrumb, Input} from "antd";
import {Link} from "react-router-dom";
import {
  useDisconnectTelegram,
  useGetIntegrationsStatus,
  useGetTelegramGroups,
  useGetTelegramStatus,
  useRequestTelegramCode,
  useVerifyTelegramCode,
} from "../../../../../react-query-hooks/integrations/integrations";
import yup from "../../../../../utils/yup-extended";
import {DashboardWrapper} from "../../../../../layouts/dashboard-wrapper";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {
  StyledAutomationsPageContent,
  StyledIntegrationConnectButton,
  StyledIntegrationConnectPanel,
  StyledIntegrationDetailCard,
  StyledIntegrationDetailHeader,
  StyledIntegrationDetailLayout,
  StyledIntegrationField,
  StyledIntegrationStatusBanner,
} from "../../automations.style";
import {TelegramIcon} from "../../icons";
import {IntegrationConnectionVisual} from "../components/IntegrationConnectionVisual";
import {IntegrationConnectPanel} from "../components/IntegrationConnectPanel";
import {FormFieldError} from "../../../../../design-system/form-field";
import {InfoCircleOutlined} from "@ant-design/icons";

const BULLET_POINTS = [
  "Once connected, add the Coachli bot to your Telegram group as an admin to get started",
  "The bot automatically adds customers, sends offering details, delivers reminders, and removes customers when access expires",
  "Assign a Telegram group to any offering when creating or editing",
  "Customers are automatically added to your linked Telegram group after every purchase",
];

function normalizeTelegramUsername(username: string | null | undefined) {
  return username?.trim().replace(/^@+/, "") ?? "";
}

type PhoneFormValues = {
  phoneNumber: string;
};

type CodeFormValues = {
  phoneCode: string;
};

const phoneSchema = yup.object().shape({
  phoneNumber: yup
    .string()
    .trim()
    .required("Phone number is required")
    .matches(
      /^\+[1-9]\d{6,14}$/,
      "Enter a valid phone number with country code, e.g. +2349060619317"
    ),
});

const codeSchema = yup.object().shape({
  phoneCode: yup
    .string()
    .trim()
    .required("Verification code is required")
    .matches(/^\d{3,10}$/, "Enter the verification code sent to your phone"),
});

export function TelegramIntegrationPage() {
  const integrationsStatusQuery = useGetIntegrationsStatus();
  const telegramStatusQuery = useGetTelegramStatus();
  const requestCodeMutation = useRequestTelegramCode();
  const verifyCodeMutation = useVerifyTelegramCode();
  const disconnectMutation = useDisconnectTelegram();

  const isConnected =
    telegramStatusQuery.data?.connected ??
    Boolean(integrationsStatusQuery.data?.telegramConnected);
  const telegramGroupsQuery = useGetTelegramGroups({enabled: isConnected});
  const telegramGroupCount =
    telegramGroupsQuery.data?.length ?? telegramStatusQuery.data?.groupCount ?? 0;
  const telegramUsername = normalizeTelegramUsername(
    telegramStatusQuery.data?.telegramUsername
  );
  const connectedStatusMessage = (
    <>
      {telegramUsername ? (
        <>
          Connected as{" "}
          <span className="integration-status-emphasis">@{telegramUsername}</span>.
        </>
      ) : (
        "Telegram is connected."
      )}{" "}
      Coachli bot found in{" "}
      <span className="integration-status-emphasis">{telegramGroupCount}</span>{" "}
      {telegramGroupCount === 1 ? "group" : "groups"}.
    </>
  );

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCodeHash, setPhoneCodeHash] = useState<string | null>(null);

  const phoneForm = useForm<PhoneFormValues>({
    resolver: yupResolver(phoneSchema),
    mode: "onSubmit",
    defaultValues: {phoneNumber: ""},
  });

  const codeForm = useForm<CodeFormValues>({
    resolver: yupResolver(codeSchema),
    mode: "onSubmit",
    defaultValues: {phoneCode: ""},
  });

  const codeStepActive = Boolean(phoneCodeHash) && !isConnected;

  function handleRequestCode(values: PhoneFormValues) {
    const normalizedPhone = values.phoneNumber.trim();
    requestCodeMutation.mutate(
      {phoneNumber: normalizedPhone},
      {
        onSuccess: (data) => {
          setPhoneNumber(normalizedPhone);
          setPhoneCodeHash(data.phoneCodeHash);
          codeForm.reset({phoneCode: ""});
        },
      }
    );
  }

  function handleResendCode() {
    if (!phoneNumber) return;
    requestCodeMutation.mutate(
      {phoneNumber},
      {
        onSuccess: (data) => {
          setPhoneCodeHash(data.phoneCodeHash);
          codeForm.reset({phoneCode: ""});
        },
      }
    );
  }

  function handleVerifyCode(values: CodeFormValues) {
    if (!phoneCodeHash || !phoneNumber) return;
    verifyCodeMutation.mutate(
      {
        phoneNumber,
        phoneCodeHash,
        phoneCode: values.phoneCode.trim(),
      },
      {
        onSuccess: () => {
          setPhoneCodeHash(null);
          setPhoneNumber("");
          phoneForm.reset({phoneNumber: ""});
          codeForm.reset({phoneCode: ""});
        },
      }
    );
  }

  function handleChangePhone() {
    setPhoneCodeHash(null);
    codeForm.reset({phoneCode: ""});
  }

  function handleDisconnect() {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        setPhoneCodeHash(null);
        setPhoneNumber("");
        phoneForm.reset({phoneNumber: ""});
        codeForm.reset({phoneCode: ""});
      },
    });
  }

  return (
    <DashboardWrapper pageTitle="Telegram">
      <StyledPageHeader>Automations</StyledPageHeader>
      <StyledAutomationsPageContent>
        <StyledIntegrationDetailHeader>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/automations?tab=integrations">Integrations</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Telegram</Breadcrumb.Item>
          </Breadcrumb>
        </StyledIntegrationDetailHeader>

        <StyledIntegrationDetailCard>
          <StyledIntegrationDetailLayout>
            <IntegrationConnectionVisual integrationIcon={<TelegramIcon />} />

            {isConnected ? (
              <IntegrationConnectPanel
                title="Automate your Telegram group access"
                bulletPoints={BULLET_POINTS}
                connectLabel="Connect Telegram"
                disconnectLabel="Disconnect Telegram"
                isConnected
                onConnect={() => undefined}
                onDisconnect={handleDisconnect}
                isDisconnectLoading={disconnectMutation.isLoading}
                connectedStatusMessage={connectedStatusMessage}
              />
            ) : (
              <StyledIntegrationConnectPanel>
                <h2>Automate your Telegram group access</h2>
                <ul>
                  {BULLET_POINTS.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                {!codeStepActive ? (
                  <form onSubmit={phoneForm.handleSubmit(handleRequestCode)}>
                    <Controller
                      control={phoneForm.control}
                      name="phoneNumber"
                      render={({
                        field: {onChange, onBlur, value, ref},
                        fieldState: {error},
                      }) => (
                        <StyledIntegrationField>
                          <label htmlFor="telegramPhoneNumber">Phone number</label>
                          <Input
                            id="telegramPhoneNumber"
                            ref={ref}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            status={error?.message ? "error" : ""}
                            placeholder="e.g. +2349060619317"
                            disabled={requestCodeMutation.isLoading}
                          />
                          <FormFieldError errorMessage={error?.message} />
                          <p className="integration-field-helper">
                            Include your country code (E.164 format).
                          </p>
                        </StyledIntegrationField>
                      )}
                    />
                    <StyledIntegrationConnectButton
                      type="primary"
                      htmlType="submit"
                      loading={requestCodeMutation.isLoading}
                      disabled={requestCodeMutation.isLoading}
                      style={{marginTop: "1.6rem"}}
                    >
                      Send verification code
                    </StyledIntegrationConnectButton>
                  </form>
                ) : (
                  <form onSubmit={codeForm.handleSubmit(handleVerifyCode)}>
                    <StyledIntegrationStatusBanner $variant="info">
                      <InfoCircleOutlined className="integration-status-banner-icon" />
                      <span>
                        We sent a verification code to <strong>{phoneNumber}</strong>.
                      </span>
                    </StyledIntegrationStatusBanner>

                    <Controller
                      control={codeForm.control}
                      name="phoneCode"
                      render={({
                        field: {onChange, onBlur, value, ref},
                        fieldState: {error},
                      }) => (
                        <StyledIntegrationField style={{marginTop: "1.6rem"}}>
                          <label htmlFor="telegramPhoneCode">Verification code</label>
                          <Input
                            id="telegramPhoneCode"
                            ref={ref}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            status={error?.message ? "error" : ""}
                            placeholder="Enter the code from Telegram"
                            disabled={verifyCodeMutation.isLoading}
                          />
                          <FormFieldError errorMessage={error?.message} />
                        </StyledIntegrationField>
                      )}
                    />

                    <StyledIntegrationConnectButton
                      type="primary"
                      htmlType="submit"
                      loading={verifyCodeMutation.isLoading}
                      disabled={
                        verifyCodeMutation.isLoading || requestCodeMutation.isLoading
                      }
                      style={{marginTop: "1.6rem"}}
                    >
                      Verify &amp; connect
                    </StyledIntegrationConnectButton>

                    <StyledIntegrationConnectButton
                      type="default"
                      htmlType="button"
                      onClick={handleResendCode}
                      loading={requestCodeMutation.isLoading}
                      disabled={
                        verifyCodeMutation.isLoading || requestCodeMutation.isLoading
                      }
                      style={{marginTop: "0.8rem"}}
                    >
                      Resend code
                    </StyledIntegrationConnectButton>

                    <StyledIntegrationConnectButton
                      type="link"
                      htmlType="button"
                      onClick={handleChangePhone}
                      disabled={
                        verifyCodeMutation.isLoading || requestCodeMutation.isLoading
                      }
                      style={{marginTop: "0.4rem"}}
                    >
                      Use a different phone number
                    </StyledIntegrationConnectButton>
                  </form>
                )}
              </StyledIntegrationConnectPanel>
            )}
          </StyledIntegrationDetailLayout>
        </StyledIntegrationDetailCard>
      </StyledAutomationsPageContent>
    </DashboardWrapper>
  );
}
